import React from 'react';
import styles from './TextEffectsOutlineOut.module.scss';
import type { TextEffectsSharedProps } from '../../TextEffectsShared/TextEffectsShared.types';
import TextEffectsShared from '../../TextEffectsShared/viewer/TextEffectsShared';
import { angleConfig, TestIds } from '../constants';
import { blendModes } from '../../TextEffectsShared/constants';
import useUniqueSvg, {
  createSvgManager,
} from '../../TextEffectsShared/viewer/useUniqueSvg';
import cx from 'clsx';
import { range } from '../../TextEffectsShared/editor/panels/design/utils';
import { usePatterns, Patterns } from '../../TextEffectsShared/viewer/patterns';

const azimuthFilters = range(
  angleConfig.start,
  angleConfig.end,
  angleConfig.step,
)
  .map(azimuth => {
    return `
    <filter id="outline-out-lightA3_azimuth:${azimuth}">
      <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="B" />
      <feSpecularLighting in="B" specularExponent="128" result="S">
        <feDistantLight azimuth="${azimuth}" elevation="72" />
      </feSpecularLighting>
      <feComposite in="S" in2="SourceAlpha" operator="in" result="L" />
      <feComposite
        in="SourceGraphic"
        in2="L"
        operator="arithmetic"
        k2="1"
        k3="1"
      />
    </filter>
  `;
  })
  .join('\n');

const blendModeFilters = blendModes
  .map(value => {
    return `
      <filter
          id="outline-out-dark-glass_blend-mode:${value}"}
        >
          <feBlend result="A" mode="screen" in2="SourceGraphic" />
          <feGaussianBlur stdDeviation="2" result="B" />
          <feComposite operator="xor" in="B" in2="A" result="C" />
          <feComposite result="fC" in="C" operator="xor" in2="C" />
          <feGaussianBlur result="B" stdDeviation="3" in="fC" />
          <feSpecularLighting
            result="S"
            specularExponent="55"
            specularConstant="1.5"
            surfaceScale="6"
            in="B"
          >
            <fePointLight z="20000" y="-8000" x="-5000" />
          </feSpecularLighting>
          <feComposite
            in="B"
            k3="1"
            k2="1.5"
            operator="arithmetic"
            in2="SourceGraphic"
            result="C"
          />
          <feComposite in="S" operator="atop" in2="C" result="C" />
          <feBlend mode="${value}" in2="C" />
        </filter>
      `;
  })
  .join('\n');

const svgManager = createSvgManager(`
  <defs>
    ${azimuthFilters}
    ${blendModeFilters}
  </defs>
`);

export type TextEffectsOutlineOutProps = TextEffectsSharedProps;

const TextEffectsOutlineOut: React.FC<TextEffectsSharedProps> = props => {
  const { content, arePatternsDisabled } = usePatterns(props);

  useUniqueSvg(svgManager);

  return (
    <TextEffectsShared
      {...props}
      arePatternsDisabled={arePatternsDisabled}
      tagProps={{
        'data-testid': TestIds.viewer.container,
      }}
      data-effect-type="outlineOut"
    >
      <Patterns
        content={content}
        arePatternsDisabled={arePatternsDisabled}
        patternScope={props.patternScope}
        unitProps={data => ({
          className: styles.container,
          'data-text': data,
          disabledPattern: arePatternsDisabled,
        })}
        unitPredicate={data => (
          <span
            className={cx(styles.text, styles.block)}
            data-testid={TestIds.viewer.text}
          >
            {data}
          </span>
        )}
      />
    </TextEffectsShared>
  );
};

export default TextEffectsOutlineOut;
